// Central runtime config. Every hardcoded URL, credential, or flag in the
// app should come from here — nothing else reads ``import.meta.env`` directly.
//
// Vite exposes only variables prefixed with ``VITE_``, so server-only secrets
// cannot accidentally leak to the client bundle. Because env vars are frozen
// at ``vite build`` time, the Docker image must be rebuilt when any of these
// change (the Dockerfile accepts them as ``--build-arg``).
//
// If you add a new configurable knob:
//
//   1. Add ``VITE_<NAME>`` to ``.env.example`` and the Dockerfile's ``ARG``
//      block.
//   2. Export it from this module; if it MUST be set in production, add it
//      to ``REQUIRED_IN_PROD`` so a misconfigured build fails loudly.
//   3. Import it from your feature — never read ``import.meta.env`` again.

const env = import.meta.env ?? {};

export const APP_ENV = env.VITE_APP_ENV || 'development';
export const IS_PROD = APP_ENV === 'production';

export const API_BASE = (env.VITE_API_BASE_URL || 'http://localhost:8080').replace(/\/+$/, '');
export const AI_BASE = (env.VITE_AI_BASE_URL || 'http://localhost:8000').replace(/\/+$/, '');
export const COLLAB_WS_URL = env.VITE_COLLAB_WS_URL || 'ws://localhost:1234';
export const MEILI_HOST = env.VITE_MEILI_HOST || 'http://localhost:7700';
export const MEILI_SEARCH_KEY = env.VITE_MEILI_SEARCH_KEY || '';
export const JITSI_DOMAIN = env.VITE_JITSI_DOMAIN || 'meet.jit.si';

export const STRIPE_PUBLIC_KEY = env.VITE_STRIPE_PUBLIC_KEY || '';

export const LOG_LEVEL = env.VITE_LOG_LEVEL || (IS_PROD ? 'warn' : 'debug');

// Variables that must not fall back to localhost / empty in a production
// build. Checked by ``validateConfig()`` below — ``main.jsx`` calls this
// before rendering so a misconfigured deploy shows a config-error screen
// instead of silently pointing at ``localhost``. Closes AUDIT.md H6.
//
// The previous list also required VITE_FIREBASE_* vars; those were dead
// weight after the Phase 2 migration (Firebase Auth + Firestore + Storage
// are all removed) and would have blocked a production boot on config
// nobody reads.
const REQUIRED_IN_PROD = [
  ['VITE_API_BASE_URL', env.VITE_API_BASE_URL],
  ['VITE_AI_BASE_URL', env.VITE_AI_BASE_URL],
];

export function validateConfig() {
  if (!IS_PROD) return { ok: true, missing: [] };
  const missing = REQUIRED_IN_PROD
    .filter(([, value]) => !value)
    .map(([name]) => name);
  return { ok: missing.length === 0, missing };
}
